import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { tokens } from "@core/theme/tokens";
import { ApiError } from "@core/http";
import { setPasswordWithToken } from "./api";
import { AccessLayout } from "./AccessLayout";
import { BigButton, Notice, PasswordField } from "./primitives";

// SPEC-221 §4: "un campo, con la validación de longitud mínima mostrada
// como ayuda antes de enviar, no después de fallar" — a propósito un solo
// campo, sin repetir contraseña (el prototipo del validador trae dos; la
// SPEC pide uno para esta pantalla y se prioriza el texto de la SPEC).
// El mínimo (8) viene del propio schema del contrato
// (api/contracts/parts/00-base.yaml:302, `password: minLength 8`) — no hay
// endpoint que exponga `security.password_min_length` al portal.
const MIN_LENGTH = 8;

export function NewPasswordScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const ready = password.length >= MIN_LENGTH;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) return;
    setError(null);
    setSubmitting(true);
    try {
      // El campo único de la pantalla cubre `password` y `confirmation` con
      // el mismo valor: el contrato exige ambos, pero SPEC-221 §4 pide un
      // solo campo visible.
      await setPasswordWithToken(token, password, password);
      navigate("/access", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("El enlace no es válido o ya venció. Solicita uno nuevo.");
      } else if (err instanceof ApiError && err.status === 400) {
        setError(
          `La contraseña debe tener al menos ${String(MIN_LENGTH)} caracteres.`,
        );
      } else {
        setError("No se pudo establecer la contraseña. Intenta de nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AccessLayout sub="Nueva contraseña">
      <form onSubmit={(event) => void handleSubmit(event)}>
        <h1
          style={{
            font: `300 27px/1.2 ${tokens.font.display}`,
            color: tokens.text.primary,
            margin: "8px 0 12px",
          }}
        >
          Nueva contraseña
        </h1>
        <p
          style={{
            font: `400 14px/1.6 ${tokens.font.ui}`,
            color: tokens.text.secondary,
            margin: "0 0 22px",
          }}
        >
          Usa al menos {MIN_LENGTH} caracteres.
        </p>
        {error ? <Notice>{error}</Notice> : null}
        <PasswordField
          label="Contraseña nueva"
          value={password}
          onChange={(value) => {
            setPassword(value);
            setError(null);
          }}
          placeholder="••••••••"
          autoComplete="new-password"
          error={tooShort}
        />
        <div
          style={{
            font: `500 12px ${tokens.font.ui}`,
            color: tooShort ? tokens.danger.base : tokens.text.tertiary,
            margin: "-6px 0 20px",
          }}
        >
          Mínimo {MIN_LENGTH} caracteres.
        </div>
        <BigButton type="submit" disabled={!ready || submitting}>
          {submitting ? "Guardando…" : "Guardar y entrar"}
        </BigButton>
      </form>
    </AccessLayout>
  );
}
